import slugify from "slugify";

import { APP_DELETER_GROUP, PLATFORM_ADMIN_GROUP } from "../constants";
import { UserRole } from "../types/entities";
import { Profile } from "../types/profile";

/**
 * Whether the profile belongs to a platform admin (member of the global
 * `pimo-home-portal-platform-admin` group). Orthogonal to the per-client
 * UserRole. This is a UX gate only; the backend PlatformAdminGuard enforces it.
 */
export function isPlatformAdminProfile(profile?: Profile): boolean {
  return hasGlobalGroup(profile, PLATFORM_ADMIN_GROUP);
}

/** Platform admin who may also delete apps. UX gate only; the backend AppDeleterGuard enforces it. */
export function isAppDeleterProfile(profile?: Profile): boolean {
  return (
    isPlatformAdminProfile(profile) &&
    hasGlobalGroup(profile, APP_DELETER_GROUP)
  );
}

function hasGlobalGroup(profile: Profile | undefined, groupName: string) {
  const normalized = (group: string) =>
    group.startsWith("/") ? group.slice(1) : group;
  return (profile?.groups ?? []).some((g) => normalized(g) === groupName);
}

/**
 * Resolves the user's account role for a client from their Keycloak groups:
 * "Owner" / "Admin" from the matching `?role=owner` / `?role=admin` group,
 * otherwise "User". Returns undefined when the profile has no groups.
 */
export function getUserRoleFromProfile(
  profile?: Profile,
  forClientName?: string
): UserRole | undefined {
  if (!profile?.groups || profile.groups.length === 0) {
    return undefined;
  }

  let clientName = forClientName;
  if (!clientName) {
    // Fall back to the first pimo-product group to extract the client name.
    const pimoGroup = profile.groups.find((group) =>
      group.startsWith("/pimo-product/")
    );
    const afterPrefix = pimoGroup?.split("/pimo-product/").pop();
    clientName = afterPrefix?.split("?")[0].split("/").filter(Boolean)[0];
  }

  if (!clientName) {
    return undefined;
  }

  // Use the same slugify logic as the backend.
  const slug = slugify(clientName, {
    lower: true,
    strict: true,
    trim: true,
  });

  // Normalize the leading slash so this works whether Keycloak's group mapper
  // is configured with "Full group path" on or off.
  const normalized = (group: string) =>
    group.startsWith("/") ? group.slice(1) : group;
  const ownerGroup = `pimo-product/${slug}?role=owner`;
  const adminGroup = `pimo-product/${slug}?role=admin`;

  if (profile.groups.some((g) => normalized(g) === ownerGroup)) {
    return "Owner";
  }
  if (profile.groups.some((g) => normalized(g) === adminGroup)) {
    return "Admin";
  }
  return "User";
}
